import { useState, useEffect } from "react";
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { cn } from "@/lib/utils";
import { textareaStyles } from "theme/components/textarea";
import { buttonStyles } from "theme/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { ChevronLeft, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import { API_CONFIG } from "@/constants/api";
import { customInstance } from "@/lib/axios-instance";
import { isAxiosError } from "axios";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface JobOrderData {
  id: string;
  campaignName: string;
  name: string;
  approvalStatus: string;
  bgvRequirements: string;
  backfill: string;
  certifications: string;
  closedReason: string;
  confidential: boolean;
  contractType: string;
  dateFilled: string;
  datePosted: string;
  drugTestRequirement: string;
  earliestStartDateMrp: string;
  educations: string;
  employmentTypeJs: string;
  estimatedStartDate: string;
  goodToGoSkills: string;
  internalQuestionSet: string;
  jobUrl: string;
  jobAdvertisement: string;
  jobAssignmentLevel: string;
  jobLocationType: string;
  jobNumber: string;
  jobTitle: string;
  languagePreference: string;
  limitNumberOfSubmission: string;
  locationListNew: string;
  mandatorySkills: string;
  minExperience: string;
  negativeKeywords: string;
  passingScore: string;
  preferredCountryList: string;
  priority: string;
  radiusdistanceunit: string;
  radiusdistance: string;
  screeningRequirements: string;
  sendJobAlertToVmsa: boolean;
  status: string;
  talentLevel: string;
  term: string;
  totalYearsExperience: string;
  typeOfRequirement: string;
  jobsite: string;
  city: string;
  clientAddress: string;
  state: string;
  locationInfo: string; // JSON string
}

export default function CreateJobOrderPage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_UPDATE">
      <CreateJobOrderPageContent />
    </PageAccessGuard>
  );
}

function CreateJobOrderPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);

  // Initialize with empty values
  const [formData, setFormData] = useState<JobOrderData>({
    id: "",
    campaignName: "",
    name: "",
    approvalStatus: "",
    bgvRequirements: "",
    backfill: "",
    certifications: "",
    closedReason: "",
    confidential: false,
    contractType: "",
    dateFilled: "",
    datePosted: "",
    drugTestRequirement: "",
    earliestStartDateMrp: "",
    educations: "",
    employmentTypeJs: "",
    estimatedStartDate: "",
    goodToGoSkills: "",
    internalQuestionSet: "",
    jobUrl: "",
    jobAdvertisement: "",
    jobAssignmentLevel: "",
    jobLocationType: "",
    jobNumber: "",
    jobTitle: "",
    languagePreference: "",
    limitNumberOfSubmission: "",
    locationListNew: "",
    mandatorySkills: "",
    minExperience: "",
    negativeKeywords: "",
    passingScore: "",
    preferredCountryList: "",
    priority: "",
    radiusdistanceunit: "",
    radiusdistance: "",
    screeningRequirements: "",
    sendJobAlertToVmsa: false,
    status: "",
    talentLevel: "",
    term: "",
    totalYearsExperience: "",
    typeOfRequirement: "",
    jobsite: "",
    city: "",
    clientAddress: "",
    state: "",
    locationInfo: ""
  });

  // Handle copy functionality
  useEffect(() => {
    if (location.state?.copyFrom) {
      const copyData = location.state.copyFrom;
      // Generate new ID for copy
      setFormData(prev => ({
        ...prev,
        id: `${prev.id}_copy_${Date.now()}`,
        campaignName: copyData.campaignName || prev.campaignName,
        jobNumber: `${prev.jobNumber}_copy`,
        jobTitle: `${copyData.jobTitle || prev.jobTitle} (Copy)`
      }));
    }
  }, [location.state]);

  const handleInputChange = (field: keyof JobOrderData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      // Parse locationInfo to ensure it's valid JSON
      let parsedLocationInfo;
      try {
        parsedLocationInfo = JSON.parse(formData.locationInfo);
      } catch (error) {
        toast.error("Invalid JSON in Location Information field");
        return;
      }

      const submitData = {
        ...formData,
        locationInfo: parsedLocationInfo
      };

      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.TEMP_JOB_ORDER}/id/0`,
        method: 'POST',
        data: submitData
      });

      toast.success("Job order created successfully!");
      navigate(ROUTES.TESTING_JOB_ORDER_MGMT);
    } catch (error) {
      console.error('Error creating job order:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to create job order";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to create job order: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to create job order: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to create job order: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to create job order: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.TESTING_JOB_ORDER_MGMT)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Job Orders
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Add Job Order</h1>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className={cn(buttonStyles, "bg-green-600 hover:bg-green-700 text-white")}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Save Job Order"}
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Job Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="id">ID</label>
                <input
                  id="id"
                  value={formData.id}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('id', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="campaignName">Campaign</label>
                <input
                  id="campaignName"
                  value={formData.campaignName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('campaignName', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  id="jobTitle"
                  value={formData.jobTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('jobTitle', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="jobNumber">Job Number</label>
                <input
                  id="jobNumber"
                  value={formData.jobNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('jobNumber', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="status">Status</label>
                <input
                  id="status"
                  value={formData.status}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('status', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="priority">Priority</label>
                <input
                  id="priority"
                  value={formData.priority}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('priority', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="employmentTypeJs">Employment Type</label>
                <input
                  id="employmentTypeJs"
                  value={formData.employmentTypeJs}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('employmentTypeJs', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="jobLocationType">Job Location Type</label>
                <input
                  id="jobLocationType"
                  value={formData.jobLocationType}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('jobLocationType', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="jobAdvertisement">Job Advertisement</label>
                <textarea
                  id="jobAdvertisement"
                  value={formData.jobAdvertisement}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('jobAdvertisement', e.target.value)}
                  className={cn(textareaStyles, "min-h-[150px]")}
                />
              </div>

              <div>
                <label htmlFor="mandatorySkills">Mandatory Skills</label>
                <textarea
                  id="mandatorySkills"
                  value={formData.mandatorySkills}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('mandatorySkills', e.target.value)}
                  className={cn(textareaStyles, "min-h-[100px]")}
                />
              </div>

              <div>
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  value={formData.city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('city', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="state">State</label>
                <input
                  id="state"
                  value={formData.state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('state', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="locationInfo">Location Information (JSON)</label>
                <textarea
                  id="locationInfo"
                  value={formData.locationInfo}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('locationInfo', e.target.value)}
                  className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                  placeholder="Enter location information as JSON object..."
                />
              </div>

              <div>
                <label htmlFor="datePosted">Date Posted</label>
                <input
                  id="datePosted"
                  type="date"
                  value={formData.datePosted}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('datePosted', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="estimatedStartDate">Estimated Start Date</label>
                <input
                  id="estimatedStartDate"
                  type="date"
                  value={formData.estimatedStartDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('estimatedStartDate', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}